import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchCollection, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import TestimonialCard from '../components/TestimonialCard';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const [page, expList, testimonialList] = await Promise.all([
          fetchPageBySlug('forside'),
          fetchCollection('experience'),
          fetchCollection('testimonials')
        ]);
        setPageData(page);
        setExperiences(expList.filter(e => e.featured));
        setTestimonials(testimonialList);
      } catch (err) {
        console.error('Error loading home page data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Insightly ApS hjælper virksomheder med ERP-ledelse, Data Migration, Dynamics 365 Finance & Operations samt Agile og SCRUM coaching.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite overflow-hidden">
      {/* 1. Hero Section (With Overlapping Cards) */}
      <section className="relative min-h-[85vh] flex flex-col items-center justify-center bg-navy-900 text-white pt-24 pb-44 lg:pt-32 lg:pb-52">
        {/* Background Layer with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/scandinavian-office.jpg"
            alt="Insightly ApS Hero Background"
            className="w-full h-full object-cover opacity-25 mix-blend-multiply"
          />
          <div className="absolute inset-0 bg-navy-900/80"></div>
        </div>

        {/* Foreground Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center flex flex-col items-center justify-center">
          {/* Large Serif Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-white leading-[1.15] tracking-tight max-w-4xl">
            {pageData ? getLocalizedValue(pageData.title, language) : t.home.heroTitle}
          </h1>

          {/* Subtitle */}
          <div className="text-base sm:text-lg text-offwhite/80 leading-relaxed max-w-2xl mt-6 mb-10 font-sans" dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : t.home.heroSubtitle }} />

          {/* Horizontal Button Group */}
          <div className="flex flex-wrap gap-4 justify-center mb-20">
            <Link
              to="/kontakt"
              className="bg-petrol text-white font-bold px-8 py-4 tracking-wider text-xs uppercase rounded-none hover:bg-petrol-dark transition-all duration-300 shadow-md flex items-center gap-2"
            >
              <i className="fa-solid fa-calendar-lines-pen"></i>
              <span>{t.home.heroCta2}</span>
            </Link>
            <a
              href="https://dk.linkedin.com/in/rene-vilsgaard/da"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-navy-900 font-bold px-8 py-4 tracking-wider text-xs uppercase rounded-none hover:bg-gray-100 transition-all duration-300 shadow-md flex items-center gap-2"
            >
              <i className="fa-brands fa-linkedin text-base text-[#0077B5]"></i>
              <span>{t.home.heroCta3}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Card Overlay Integration */}
      <div className="relative -mt-32 sm:-mt-40 z-10 w-full max-w-6xl mx-auto px-4 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1 */}
          <div className="bg-white rounded-none p-8 md:p-10 shadow-2xl border border-bluegray/20 relative group hover:-translate-y-1 transition-transform duration-300">
            <span className="text-bluegray/30 text-6xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">01</span>
            <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
              <i className="fa-solid fa-diagram-project text-lg text-petrol"></i>
            </div>
            <h3 className="font-serif text-xl font-bold text-navy-900 mb-3">{t.home.erpTitle}</h3>
            <p className="text-charcoal/80 text-sm leading-relaxed">
              {t.home.erpDesc}
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-white rounded-none p-8 md:p-10 shadow-2xl border border-bluegray/20 relative group hover:-translate-y-1 transition-transform duration-300">
            <span className="text-bluegray/30 text-6xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">02</span>
            <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
              <i className="fa-solid fa-database text-lg text-petrol"></i>
            </div>
            <h3 className="font-serif text-xl font-bold text-navy-900 mb-3">{t.home.dataTitle}</h3>
            <p className="text-charcoal/80 text-sm leading-relaxed">
              {t.home.dataDesc}
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-none p-8 md:p-10 shadow-2xl border border-bluegray/20 relative group hover:-translate-y-1 transition-transform duration-300">
            <span className="text-bluegray/30 text-6xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">03</span>
            <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
              <i className="fa-solid fa-arrows-spin text-lg text-petrol"></i>
            </div>
            <h3 className="font-serif text-xl font-bold text-navy-900 mb-3">{t.home.agileTitle}</h3>
            <p className="text-charcoal/80 text-sm leading-relaxed">
              {t.home.agileDesc}
            </p>
          </div>
        </div>
      </div>

      {/* 2. Transition White Space & Primary Accent Banner */}
      <div className="h-12 bg-white"></div>
      <section className="bg-navy-900 py-16 text-white text-center relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-medium leading-tight text-white">
            {t.home.valuePropositionQuote}
          </h2>
          <p className="text-offwhite/70 text-sm sm:text-base max-w-2xl mx-auto font-sans leading-relaxed pt-2">
            {t.home.valuePropositionText}
          </p>
        </div>
      </section>

      {/* 3. Middle Media & Case Studies Header Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Asymmetrical Layout: Right Aligned Media Block & Left Text */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.experienceDomainTag}</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-medium text-navy-900 leading-tight">
                {t.home.experienceDomainTitle}
              </h2>
              <p className="text-charcoal/80 text-base leading-relaxed font-sans">
                {t.home.experienceDomainText}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                <div className="p-5 bg-offwhite rounded-none border border-bluegray/60">
                  <h4 className="font-serif text-base font-bold text-navy-900 mb-1">{t.home.deliveryTitle}</h4>
                  <p className="text-xs text-charcoal/70">{t.home.deliveryText}</p>
                </div>
                <div className="p-5 bg-offwhite rounded-none border border-bluegray/60">
                  <h4 className="font-serif text-base font-bold text-navy-900 mb-1">{t.home.governanceTitle}</h4>
                  <p className="text-xs text-charcoal/70">{t.home.governanceText}</p>
                </div>
              </div>
            </div>

            {/* Asymmetrical Media Block (Right Aligned) */}
            <div className="lg:col-span-5">
              <div className="relative rounded-none overflow-hidden shadow-2xl border border-bluegray">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/scandinavian-office.jpg"
                  alt="Insightly ApS - René Vilsgaard"
                  className="w-full h-[400px] object-cover rounded-none"
                />
              </div>
            </div>
          </div>

          {/* Section Header Block (Centered Bottom) */}
          <div className="flex flex-col items-center text-center mt-24 mb-16">
            <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.servicesTag}</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-medium text-navy-900 mt-3">
              {t.home.servicesTitle2}
            </h2>
            <p className="text-charcoal/60 text-sm max-w-xl mx-auto mt-2 font-sans">
              {t.home.servicesSubtitle2}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Service 1 */}
            <Link to="/erp-lead" className="bg-white rounded-none border border-bluegray p-8 flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-300 relative group text-left">
              <span className="text-bluegray/20 text-5xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">01</span>
              <div>
                <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
                  <i className="fa-solid fa-diagram-project text-base text-petrol"></i>
                </div>
                <h3 className="font-serif text-lg font-bold text-navy-900 mb-3">{t.home.erpTitle}</h3>
                <p className="text-xs text-charcoal/70 leading-relaxed mb-6">
                  {t.home.erpServiceDesc}
                </p>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-petrol group-hover:text-navy-900 flex items-center gap-1.5 transition-colors">
                <span>{t.home.readMore}</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </div>
            </Link>

            {/* Service 2 */}
            <Link to="/data-migration" className="bg-white rounded-none border border-bluegray p-8 flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-300 relative group text-left">
              <span className="text-bluegray/20 text-5xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">02</span>
              <div>
                <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
                  <i className="fa-solid fa-database text-base text-petrol"></i>
                </div>
                <h3 className="font-serif text-lg font-bold text-navy-900 mb-3">{t.home.dataTitle}</h3>
                <p className="text-xs text-charcoal/70 leading-relaxed mb-6">
                  {t.home.dataServiceDesc}
                </p>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-petrol group-hover:text-navy-900 flex items-center gap-1.5 transition-colors">
                <span>{t.home.readMore}</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </div>
            </Link>

            {/* Service 3 */}
            <Link to="/erfaring" className="bg-white rounded-none border border-bluegray p-8 flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-300 relative group text-left">
              <span className="text-bluegray/20 text-5xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">03</span>
              <div>
                <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
                  <i className="fa-solid fa-list-check text-base text-petrol"></i>
                </div>
                <h3 className="font-serif text-lg font-bold text-navy-900 mb-3">{t.home.pmTitle}</h3>
                <p className="text-xs text-charcoal/70 leading-relaxed mb-6">
                  {t.home.pmServiceDesc}
                </p>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-petrol group-hover:text-navy-900 flex items-center gap-1.5 transition-colors">
                <span>{t.home.seeExperience}</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </div>
            </Link>

            {/* Service 4 */}
            <Link to="/agile-scrum-coach" className="bg-white rounded-none border border-bluegray p-8 flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-300 relative group text-left">
              <span className="text-bluegray/20 text-5xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">04</span>
              <div>
                <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none mb-6">
                  <i className="fa-solid fa-arrows-spin text-base text-petrol"></i>
                </div>
                <h3 className="font-serif text-lg font-bold text-navy-900 mb-3">{t.home.agileTitle}</h3>
                <p className="text-xs text-charcoal/70 leading-relaxed mb-6">
                  {t.home.agileServiceDesc}
                </p>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-petrol group-hover:text-navy-900 flex items-center gap-1.5 transition-colors">
                <span>{t.home.readMore}</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Mid-Page Dark Overlay Banner */}
      <section className="relative h-[550px] flex items-center justify-center text-white text-center py-24 overflow-hidden">
        {/* Fixed Background Image */}
        <div 
          className="absolute inset-0 z-0 bg-fixed bg-cover bg-center animate-fade-in"
          style={{ 
            backgroundImage: "url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/agile-scrum-team.jpg')" 
          }}
        ></div>
        {/* Darker overlay */}
        <div className="absolute inset-0 bg-navy-900/80 z-1"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol-light font-bold">// {t.home.strategicTag}</span>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-medium leading-tight text-white">
            {t.home.strategicTitle}
          </h3>
          <p className="text-offwhite/80 text-sm sm:text-base max-w-xl mx-auto font-sans leading-relaxed">
            {t.home.strategicText}
          </p>
        </div>
      </section>

      {/* 5. Team Introduction Header Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Grid/Flex Split */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-end border-b border-bluegray pb-8 mb-12">
            <div className="space-y-2">
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.personTag}</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-medium text-navy-900">
                {t.home.personTitle}
              </h2>
            </div>
            <Link
              to="/om-rene"
              className="mt-4 md:mt-0 bg-navy-900 text-white tracking-widest text-xs font-bold px-8 py-4 uppercase rounded-none hover:bg-petrol transition-colors duration-300 shadow-md inline-block text-center"
            >
              {t.home.personCta}
            </Link>
          </div>

          {/* Personal Bio Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column - Image */}
            <div className="lg:col-span-5">
              <div className="relative rounded-none overflow-hidden shadow-2xl border border-bluegray">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/imgi-2-rvi-profilbillede.png"
                  alt="René Vilsgaard - Menneskeligt fokus"
                  className="w-full h-[450px] object-cover rounded-none"
                />
              </div>
            </div>

            {/* Right Column - Text */}
            <div className="lg:col-span-7 space-y-6">
              <h3 className="text-2xl font-serif font-medium text-navy-900">
                {t.home.personSubtitle}
              </h3>
              <p className="text-charcoal/80 text-base leading-relaxed font-sans" dangerouslySetInnerHTML={{ __html: t.home.personText1 }} />
              <p className="text-charcoal/80 text-base leading-relaxed font-sans">
                {t.home.personText2}
              </p>
              <div className="pt-4">
                <Link
                  to="/om-rene"
                  className="inline-flex items-center gap-2 bg-navy-900 hover:bg-petrol text-offwhite px-6 py-3.5 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-sm"
                >
                  <span>{t.home.personCta}</span>
                  <i className="fa-solid fa-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-offwhite-dark border-t border-b border-bluegray">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4 border-b border-bluegray/60 pb-8">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.customersTag}</span>
                <h2 className="text-3xl sm:text-4xl font-serif font-medium text-navy-900 mt-2">
                  {t.home.customersTitle}
                </h2>
              </div>
              <p className="text-sm text-charcoal/60 max-w-md font-sans leading-relaxed">
                {t.home.customersSubtitle}
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
              {testimonials.map((item, idx) => (
                <TestimonialCard key={item.id || idx} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Experience / Cases Section */}
      <section className="py-24 bg-offwhite border-t border-b border-bluegray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4 border-b border-bluegray/60 pb-8">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.experienceTag}</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-medium text-navy-900 mt-2">
                {t.home.experienceTitle}
              </h2>
            </div>
            <Link
              to="/erfaring"
              className="text-xs font-bold uppercase tracking-wider text-petrol hover:text-navy-900 flex items-center gap-1.5 transition-colors"
            >
              <span>{t.home.experienceCta}</span>
              <i className="fa-solid fa-arrow-right text-[10px]"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {experiences.map((exp, idx) => (
              <div key={exp.id} className="bg-white rounded-none border border-bluegray p-8 shadow-xl flex flex-col justify-between relative">
                <span className="text-bluegray/20 text-5xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">0{idx + 1}</span>
                <div>
                  <div className="flex justify-between items-start gap-4 mb-6">
                    <div>
                      <span className="text-[10px] font-bold text-petrol uppercase tracking-widest bg-petrol-light px-3 py-1 rounded-none border border-petrol/20">
                        {getLocalizedValue(exp.period, language)}
                      </span>
                      <h3 className="font-serif text-2xl font-bold text-navy-900 mt-4">{getLocalizedValue(exp.company, language)}</h3>
                      <p className="text-xs text-navy-900/60 font-bold uppercase tracking-wider mt-1">{getLocalizedValue(exp.role, language)}</p>
                    </div>
                    <div className="h-10 w-10 bg-navy-50 text-navy-900 flex items-center justify-center rounded-none shrink-0">
                      <i className="fa-solid fa-building text-base text-petrol"></i>
                    </div>
                  </div>
                  <div 
                    className="text-sm text-charcoal/80 leading-relaxed mb-6 space-y-3 font-sans"
                    dangerouslySetInnerHTML={{ __html: getLocalizedValue(exp.description, language) }}
                  />
                </div>
                <div className="border-t border-bluegray/60 pt-4 mt-auto">
                  <p className="text-[10px] font-bold text-navy-900/50 uppercase tracking-widest mb-2">// {t.home.skillsInPlay}</p>
                  <p className="text-xs text-navy-900/80 font-medium">{getLocalizedValue(exp.skills, language)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
